/**
 * A one-page availability report for the current selection, as copyable text.
 *
 * The question this answers arrives from buyers almost verbatim: "do you have
 * off-the-shelf footage available, and if so what environments and how many
 * hours of each". Everything needed is already on the Analysis tab — the
 * delivery-grade corpus, the filters, and the environment labels — so this is a
 * rendering of the selection rather than a new query.
 *
 * Plain text, not a PDF and not HTML. It gets pasted into a reply, so it has to
 * survive landing in a mail composer that will strip anything cleverer.
 *
 * No org, customer or device identity, ever: a buyer sees the shape of the
 * library, not who we collect it from.
 *
 * Deliberately bare. Every qualifier -- the visual/inferred split, the capture
 * window, the excluded and unclassified hours -- is knowable from the tab
 * itself, and a buyer-facing page reads better carrying the numbers alone. That
 * means the caveats travel with whoever sends this, not in it.
 *
 * It filters nothing of its own. What to leave out is the Environments column
 * in the tab's Selection panel, which governs the rate and the stats too; a
 * second exclusion in here would have silently disagreed with both.
 */
import React, { useMemo, useState } from 'react';

import { CorpusVideo, EnvLabel } from './whShared';
import './DatasetPage.css';

const H = (h: number) => `${Math.round(h).toLocaleString()} h`;

// Must match wh_environment.UNCONFIRMED_ENV. A real label the backend assigns,
// not the absence of one: footage we cannot state a setting for, whether because
// it has not been read, cannot be read (embeddings expired), or was read and did
// not separate. Excluded from the report by default -- an availability report is
// a claim about what we can describe, and this is the part we cannot.
const UNTAGGED_ENV = 'setting unknown';

interface Props {
  videos: CorpusVideo[];
  envs: { [fn: string]: EnvLabel };
  onClose: () => void;
}

/**
 * The FORMAT block, derived from the selection rather than asserted.
 *
 * It used to be the constant string "1080p, 30 fps", which was true only while
 * the tab could not count anything else. With the 720p tier switchable on, a
 * fixed line would tell a buyer every hour offered is 1080p while the hours
 * beneath it included footage that is not -- the one claim in this report a
 * buyer can check on delivery, and the one worth getting wrong the least.
 *
 * The frame rate moved for the same reason. With "include lower fps" on, the
 * selection carries footage measured anywhere from 8 to 28 fps, so "30 fps"
 * stops being a rounding and becomes the wrong number. There is no honest
 * single figure for that population -- it is a smear, not a second mode -- so
 * the line names it as variable and keeps 30 as the ceiling it actually is.
 */
const formatLine = (videos: CorpusVideo[]) => {
  const hd = videos.some((v) => v.grade === 1 || v.grade === 3);
  const sd = videos.some((v) => v.grade === 2 || v.grade === 4);
  const low = videos.some((v) => v.grade === 3 || v.grade === 4);
  const res = hd && sd ? '1080p and 720p' : sd ? '720p' : '1080p';
  return `  ${res}, ${low ? 'variable frame rate, up to 30 fps' : '30 fps'}`;
};

export default function ReportPanel({ videos, envs, onClose }: Props) {
  // Off by default: most enquiries are about volume and setting, and the format
  // line is the same three facts every time. Available for the enquiries that
  // do ask.
  const [withFormat, setWithFormat] = useState(false);
  // Off by default. `untagged` is footage whose embeddings expired before it
  // could be described, so it has hours but no setting -- and it is the largest
  // single bucket in the library. Listing it to a buyer answers a question they
  // did not ask ("what don't you know about your own footage") and drags the
  // percentages of everything they DID ask about downward. Available for the
  // enquiry that is about total volume rather than about settings.
  const [withUntagged, setWithUntagged] = useState(false);
  const [copied, setCopied] = useState(false);

  const stats = useMemo(() => {
    const rows = new Map<string, { hours: number }>();
    let total = 0;

    videos.forEach((v) => {
      const h = v.duration / 3600;
      const e = envs[v.filename];
      if (!e) return;   // unclassified footage is simply not reported
      // Excluded from the TOTAL as well as the table, not just hidden from the
      // rows. A total that counted hours no row accounts for would leave the
      // shares silently failing to sum, and the reader has no way to see why.
      if (!withUntagged && e.environment === UNTAGGED_ENV) return;
      total += h;
      const r = rows.get(e.environment) || { hours: 0 };
      r.hours += h;
      rows.set(e.environment, r);
    });

    return {
      rows: Array.from(rows.entries())
        .map(([label, r]) => ({ label, ...r }))
        .sort((a, b) => b.hours - a.hours),
      total,
    };
  }, [videos, envs, withUntagged]);

  const text = useMemo(() => {
    const d = new Date().toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
    const pad = Math.max(20, ...stats.rows.map((r) => r.label.length));
    const lines: string[] = [];

    lines.push('BODY-WORN CAMERA FOOTAGE — AVAILABILITY');
    lines.push(`Generated ${d}`);
    if (withFormat) {
      lines.push('');
      lines.push('FORMAT');
      // 1080p, not 1920x1080: the delivery gate is a resolution class, and the
      // pixel dimensions read as a spec sheet rather than an answer. Codec is
      // left out -- it varies across the library (h264 and hevc both ship).
      lines.push(formatLine(videos));
    }
    lines.push('');
    lines.push('AVAILABLE NOW');
    lines.push(`  ${H(stats.total)}`);
    lines.push('');
    lines.push('BY ENVIRONMENT');
    stats.rows.forEach((r) => {
      const share = stats.total ? Math.round((100 * r.hours) / stats.total) : 0;
      // A row with real hours must never read as 0% -- that says "none" about
      // footage we are offering to sell. Rounding down to zero is a display
      // artifact, so say what it is: present, under a percent.
      const pct = share === 0 && r.hours > 0 ? '<1%' : `${share}%`;
      lines.push(
        `  ${r.label.padEnd(pad)}  ${H(r.hours).padStart(9)}   ${pct.padStart(4)}`,
      );
    });
    return lines.join('\n');
  }, [stats, withFormat, videos]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked (no https / no permission) -- the text is on screen
         and selectable, which is the fallback either way */
    }
  };

  return (
    <div className="ds-modal" onClick={onClose}>
      <div
        className="ds-checkout-box ds-report-box"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ds-checkout-head">
          <b>Availability report</b>
          <button onClick={onClose}>close</button>
        </div>

        <div className="ds-report-bar">
          <div className="ds-report-toggles">
            <label className="ds-report-toggle">
              <input
                type="checkbox"
                checked={withFormat}
                onChange={(e) => setWithFormat(e.target.checked)}
              />
              include format
            </label>
            <label className="ds-report-toggle">
              <input
                type="checkbox"
                checked={withUntagged}
                onChange={(e) => setWithUntagged(e.target.checked)}
              />
              include unknown setting
            </label>
          </div>
          <button type="button" className="ds-report-copy" onClick={copy}>
            {copied ? 'copied' : 'copy'}
          </button>
        </div>

        <pre className="ds-report-text">{text}</pre>
      </div>
    </div>
  );
}
